import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, CalendarDays, Zap, Users, Settings, Menu, X,
  History, LogOut, Home, CreditCard, AlertTriangle, Sparkles,
} from "lucide-react";
import { Logo, BRAND } from "@/components/Brand";
import WorkshopNamePrompt from "@/components/WorkshopNamePrompt";

const items = [
  { to: "/panel", label: "Resumen", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/agenda", label: "Agenda", icon: CalendarDays, testid: "nav-agenda" },
  { to: "/rapida", label: "Mecánica Rápida", icon: Zap, testid: "nav-rapida" },
  { to: "/vehiculos", label: "Vehículos", icon: History, testid: "nav-vehiculos" },
  { to: "/mecanicos", label: "Mecánicos", icon: Users, testid: "nav-mecanicos" },
  { to: "/ajustes", label: "Ajustes", icon: Settings, testid: "nav-ajustes" },
  { to: "/suscripcion", label: "Suscripción", icon: CreditCard, testid: "nav-suscripcion" },
];

function PlanBanner({ workshop, onGo }) {
  if (!workshop) return null;
  const plan = workshop.plan;
  if (plan === "lifetime") return null; // silent
  if (plan === "active") return null; // silent
  const days = workshop.trial_days_left ?? 0;
  if (plan === "trial" && days > 0) {
    const urgent = days <= 15;
    return (
      <div
        className={`sticky top-0 z-30 border-b px-4 py-2.5 flex items-center justify-between gap-3 text-sm ${
          urgent ? "bg-amber-50 border-amber-200 text-amber-900" : "bg-blue-50 border-blue-200 text-blue-900"
        }`}
        data-testid="trial-banner"
      >
        <div className="flex items-center gap-2 min-w-0">
          <Sparkles className="w-4 h-4 shrink-0" />
          <span className="truncate">
            <b>Prueba gratuita</b> · Te quedan <b>{days}</b> día{days === 1 ? "" : "s"} con acceso completo.
          </span>
        </div>
        <button
          onClick={onGo}
          className={`shrink-0 px-3 py-1.5 rounded-md font-medium ${urgent ? "bg-amber-700 hover:bg-amber-800 text-white" : "bg-blue-700 hover:bg-blue-800 text-white"}`}
          data-testid="trial-banner-upgrade"
        >
          Suscribirme
        </button>
      </div>
    );
  }
  // expired
  return (
    <div className="sticky top-0 z-30 bg-red-50 border-b border-red-200 text-red-900 px-4 py-2.5 flex items-center justify-between gap-3 text-sm" data-testid="expired-banner">
      <div className="flex items-center gap-2 min-w-0">
        <AlertTriangle className="w-4 h-4 shrink-0" />
        <span className="truncate">
          <b>Plan gratuito activo</b> · Máx. 1 mecánico y 3 citas/día. Sin vacaciones ni festivos.
        </span>
      </div>
      <button
        onClick={onGo}
        className="shrink-0 px-3 py-1.5 rounded-md bg-red-700 hover:bg-red-800 text-white font-medium"
        data-testid="expired-banner-upgrade"
      >
        Suscribirme
      </button>
    </div>
  );
}

export default function Layout({ children, workshop, onLogout }) {
  const loc = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const isHome = loc.pathname === "/panel";

  const handleLogout = () => {
    if (!window.confirm("¿Salir del taller? Podrás volver a entrar con tu cuenta.")) return;
    onLogout && onLogout();
  };

  const SidebarContent = (
    <>
      <div className="p-6 border-b border-slate-800 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Logo size={40} className="shrink-0" />
          <div className="min-w-0">
            <div className="font-display font-bold text-lg leading-tight text-white truncate" data-testid="workshop-name">
              {workshop?.name || "Taller"}
            </div>
            <div className="text-xs text-slate-400 font-mono truncate" data-testid="workshop-code">{workshop?.code}</div>
          </div>
        </div>
        <button
          className="md:hidden text-slate-300 p-2"
          onClick={() => setOpen(false)}
          data-testid="close-sidebar-btn"
          aria-label="Cerrar menú"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
      <nav className="flex-1 py-4 overflow-y-auto">
        {items.map((it) => {
          const active = loc.pathname === it.to;
          const Icon = it.icon;
          return (
            <Link
              key={it.to}
              to={it.to}
              data-testid={it.testid}
              onClick={() => setOpen(false)}
              className={`flex items-center gap-3 px-6 py-3 text-sm font-medium transition-colors ${
                active
                  ? "bg-blue-600 text-white border-l-4 border-blue-300"
                  : "text-slate-300 hover:bg-slate-800 border-l-4 border-transparent"
              }`}
            >
              <Icon className="w-4 h-4" />
              {it.label}
            </Link>
          );
        })}
      </nav>
      <div className="p-4 border-t border-slate-800 space-y-2">
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-300 hover:bg-slate-800 rounded transition-colors"
          data-testid="logout-btn"
        >
          <LogOut className="w-4 h-4" /> Cerrar sesión
        </button>
        <div className="text-xs text-slate-500">v2.0 · SaaS</div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex bg-slate-50">
      <aside className="hidden md:flex w-64 bg-slate-900 text-slate-100 flex-col shrink-0" data-testid="sidebar">
        {SidebarContent}
      </aside>

      {open && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} data-testid="sidebar-overlay" />
          <aside className="relative w-64 bg-slate-900 text-slate-100 flex flex-col" data-testid="sidebar-mobile">
            {SidebarContent}
          </aside>
        </div>
      )}

      <main className="flex-1 min-w-0 overflow-auto">
        <div className="md:hidden sticky top-0 z-40 bg-slate-900 text-white flex items-center justify-between px-4 py-3 border-b border-slate-800">
          <button onClick={() => setOpen(true)} data-testid="open-sidebar-btn" aria-label="Abrir menú" className="p-2 -ml-2">
            <Menu className="w-6 h-6" />
          </button>
          <div className="flex items-center gap-2">
            <Logo size={24} />
            <span className="font-display font-bold">{BRAND.name}</span>
          </div>
          <div className="w-10" />
        </div>
        <PlanBanner workshop={workshop} onGo={() => navigate("/suscripcion")} />
        <div className="max-w-7xl mx-auto p-4 md:p-8">
          {!isHome && (
            <button
              onClick={() => navigate("/panel")}
              data-testid="back-home-btn"
              className="w-full sm:w-auto mb-6 inline-flex items-center justify-center gap-2 h-14 px-6 rounded-lg bg-slate-900 hover:bg-slate-800 text-white font-display font-bold text-base shadow-md transition-colors focus:outline-none focus:ring-4 focus:ring-slate-300"
            >
              <Home className="w-5 h-5" />
              Volver al inicio
            </button>
          )}
          {children}
        </div>
      </main>
      <WorkshopNamePrompt />
    </div>
  );
}
